import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import backgroundElems from 'modules/overlay-canvas/addons/background-elems';
import layerManager from 'modules/overlay-canvas/addons/layer-manager';
import lineDrawer from 'modules/overlay-canvas/addons/line-drawer';
import SnapToGrid from 'modules/overlay-canvas/addons/snap-to-grid';
import useFabric from 'modules/overlay-canvas/hooks/use-fabric';
import alignment from 'modules/overlay-canvas/addons/alignment/alignment';
import customization from 'modules/overlay-canvas/addons/customization';
import firstSelection from 'modules/overlay-canvas/addons/first-selection';
import history from 'modules/overlay-canvas/addons/history';
import zoom from 'modules/overlay-canvas/addons/zoom';
import { setInit, setIsEditingText, setPreviewState, setSavedScheme } from 'modules/template-editor/store/editor/editor';
import { selectPreviewState } from 'modules/template-editor/store/editor/selectors';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';
import useLocalStorage from 'modules/template-editor/ui/hooks/use-local-storage';
import useTemplates from 'modules/template-editor/ui/hooks/use-templates';
import { theme } from 'styles/theme/theme';
import { useEditorCanvasActions } from 'modules/template-editor/ui/context';

function Canvas() {
  const dispatch = useDispatch();
  const { setManager, setActiveObjects } = useEditorCanvasActions();
  const { getData } = useLocalStorage();
  const { activeTemplate } = useTemplates();
  const { activeScheme, activeBoard } = useBoards();
  const {
    scheme: templateBeforePreview,
    history: historyOfChanges,
    curHistoryIndex,
    resolution,
    backgroundColor,
    viewport,
  } = useSelector(selectPreviewState) ?? {};
  const startResolution = useMemo(() => {
    return activeTemplate ? activeTemplate.resolution : resolution;
  }, [activeTemplate, resolution]);
  const { manager, canvas } = useFabric('interactive', {
    data: {
      ...startResolution,
      backgroundColor: theme.colors.SurfaceWhite,
    },
  });

  const resizeCanvas = useCallback(() => {
    if (!manager) return;
    const { resolution } = getData();
    manager.setResolution(startResolution ?? resolution ?? { width: 1920, height: 1080 });
    manager.setDimensions({ width: window.innerWidth - 424, height: window.innerHeight - 135 });
  }, [getData, manager, startResolution]);

  useEffect(() => {
    dispatch(setInit(false));
    if (!manager) return;
    setManager(manager);

    const { scheme: schemeLocalStorage, resolution, boardType } = getData();
    const loadableScheme = templateBeforePreview ?? activeScheme ?? schemeLocalStorage ?? '';

    manager.loadLocal({ scheme: loadableScheme, safeBackground: true }).then(() => {
      const resultResolution = startResolution ?? resolution ?? manager.resolution.value;

      backgroundElems.drawBorder(manager.canvas, resultResolution);
      backgroundElems.drawGrid(manager.canvas, {
        resolution: resultResolution,
        gridStep: SnapToGrid.gridStep,
      });

      if (activeBoard?.type === 'carousel' || boardType === 'carousel') {
        backgroundElems.drawDivider(manager.canvas, {
          resolution: resultResolution,
        });
      }

      if (historyOfChanges && curHistoryIndex) {
        history.setHistory({ history: historyOfChanges, index: curHistoryIndex });
      } else {
        history.init();
      }
      if (backgroundColor) {
        manager.canvas.setBackgroundColor(backgroundColor, () => {});
      }
      if (viewport) {
        manager.canvas.setViewportTransform([...viewport]);
      }

      if (!templateBeforePreview) {
        dispatch(setSavedScheme(manager.getScheme(true)));
      }

      dispatch(setPreviewState({}));
      history.activeHistory();
      manager.normalizedViewport();
      dispatch(setInit(true));
    });

    history.loadManager(manager);
    zoom.init(manager);
    zoom.enableDragging();
    zoom.enableZoom();
    SnapToGrid.enableSnapToGrid(manager?.canvas);
    layerManager.init(manager);
    lineDrawer.init(manager.canvas);
    alignment.init(manager);

    manager.canvas.on('selection:created', (e: any) => {
      firstSelection.set(e.selected?.[0] ?? null);
      const activeObjects = manager.canvas.getActiveObjects();
      activeObjects.forEach((obj) => obj.set({ ...customization.getAllProperties() }));
      setActiveObjects(activeObjects);
    });

    manager.canvas.on('selection:updated', (e: any) => {
      const activeObjects = manager.canvas.getActiveObjects();
      activeObjects.forEach((obj) => obj.set({ ...customization.getAllProperties() }));
      setActiveObjects(activeObjects);
    });

    manager.canvas.on('selection:cleared', (_) => {
      firstSelection.set(null);
      setActiveObjects([]);
    });

    manager.canvas.on('text:editing:entered', (_) => {
      dispatch(setIsEditingText(true));
    });

    manager.canvas.on('text:editing:exited', (_) => {
      dispatch(setIsEditingText(false));
    });

    window.addEventListener('resize', resizeCanvas, false);

    resizeCanvas();
    manager.normalizedViewport();

    return () => {
      window.removeEventListener('resize', resizeCanvas, false);
      history.clearHistory();
    };

    // eslint-disable-next-line
  }, [dispatch, manager]);

  return canvas;
}

export default Canvas;
